import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "../components/common/PageHero";
import { Reveal } from "../components/common/Reveal";
import { SectionHeading } from "../components/common/SectionHeading";
import { Marquee } from "../components/common/Marquee";
import { IMAGES, LOGO_PINK } from "../lib/data";

const VALUES = [
    { title: "Fresh, always", copy: "Everything is spun and popped close to showtime — never weeks in a warehouse." },
    { title: "NYC made", copy: "Born in New York. Built for rooftops, block parties, boardrooms and bodegas." },
    { title: "For every crowd", copy: "Kids' birthdays to brand launches. The show fits the room." },
    { title: "Feed-first", copy: "If it isn't photogenic, it isn't Showtime." },
];

export const AboutPage = () => {
    useEffect(() => {
        document.title = "About — Showtime Carnival Co. | NYC Cotton Candy & Popcorn";
    }, []);

    return (
        <>
            <PageHero
                kicker="About"
                title="A Small Brand With A Big Show."
                copy="Showtime Carnival Co. is a modern NYC event and snack company bringing fresh carnival-style treats to parties, corporate events, schools, community gatherings and retail shelves."
                testid="about-hero"
            />
            <Marquee />
            <section className="bg-white py-20 md:py-28">
                <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid gap-14 lg:grid-cols-2 items-center">
                    <Reveal>
                        <div className="rounded-[2.5rem] overflow-hidden">
                            <img src={IMAGES.about} alt="Showtime popcorn machine at an event setup" loading="lazy" className="w-full aspect-[4/3] object-cover" />
                        </div>
                    </Reveal>
                    <div>
                        <SectionHeading
                            chapter="01"
                            kicker="The Story"
                            title="The Show Starts Here."
                            copy="We took the best part of the carnival — the treats — and rebuilt it as a modern NYC food brand. No clowns. No cheap rentals. Just fresh cotton candy, gourmet popcorn and a setup that turns heads."
                            testid="about-story-heading"
                        />
                        <Reveal delay={0.1}>
                            <Link
                                to="/book"
                                data-testid="about-book-cta"
                                className="inline-flex items-center gap-2 rounded-full bg-pink px-8 py-4 font-oswald uppercase tracking-widest text-ink hover:bg-ink hover:text-white transition-colors duration-300 active:scale-95"
                            >
                                Book The Show <ArrowRight size={16} />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>
            <section className="bg-ink tent-stripes text-white py-20 md:py-28">
                <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                    <SectionHeading
                        chapter="02"
                        kicker="What We Stand For"
                        title="House Rules."
                        dark
                        testid="about-values-heading"
                    />
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="about-values">
                        {VALUES.map((v, i) => (
                            <Reveal key={v.title} delay={i * 0.07}>
                                <div className="rounded-3xl border border-zinc-800 p-8 h-full hover:border-pink transition-colors duration-300">
                                    <p className="font-oswald text-pink text-sm mb-4">{String(i + 1).padStart(2, "0")}</p>
                                    <h3 className="font-oswald uppercase text-xl">{v.title}</h3>
                                    <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{v.copy}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2} className="mt-16 flex justify-center">
                        <img src={LOGO_PINK} alt="Showtime Carnival Co." className="h-28 md:h-40 w-auto" data-testid="about-logo" />
                    </Reveal>
                </div>
            </section>
        </>
    );
};
